/**
 * Mesin harga Cipta Imaji.
 *
 * Dipakai dua kali untuk setiap transaksi:
 *  - di browser, menampilkan harga live saat pelanggan mengubah pilihan
 *  - di server, menghitung ulang sebelum order disimpan
 *
 * Harga dari browser tidak pernah dipercaya. Server selalu menghitung sendiri
 * memakai fungsi ini, lalu membandingkan hasilnya.
 *
 * Semua nilai uang berupa integer rupiah. Tidak ada floating point pada uang.
 */

export type PriceUnit = 'PIECE' | 'SQUARE_METER' | 'LINEAR_METER';

export interface OptionValueSpec {
  value: string;
  priceDelta: number; // rupiah, ditambahkan ke harga satuan
  priceFactor: number; // pengali harga satuan
  widthMm?: number | null;
  heightMm?: number | null;
}

export interface OptionSpec {
  key: string;
  required: boolean;
  values: OptionValueSpec[];
}

export interface TierSpec {
  minQty: number;
  factor: number;
}

/**
 * Harga eksplisit untuk satu kombinasi opsi.
 *
 * Daftar harga Cipta Imaji sebagian besar ditentukan per kombinasi, bukan hasil
 * penjumlahan delta antar opsi. Baris seperti ini menyatakan harganya apa adanya
 * sehingga tidak ada selisih pembulatan antara katalog dan kasir.
 */
export interface PriceOverrideSpec {
  /** Kombinasi yang dicocokkan. Boleh sebagian dari seluruh opsi produk. */
  selections: Record<string, string>;
  /** Harga satuan sebelum faktor luas dan tier jumlah. */
  unitPrice: number;
  /** Harga coret untuk kombinasi ini. */
  compareAtPrice?: number | null;
}

export interface ProductPriceSpec {
  slug: string;
  priceUnit: PriceUnit;
  basePrice: number;
  /** Harga coret bawaan, dipakai bila override yang cocok tidak punya sendiri. */
  compareAtPrice?: number | null;
  minQty: number;
  qtyStep: number;
  options: OptionSpec[];
  tiers: TierSpec[];
  /** Kosong atau tidak diisi berarti harga murni dari basePrice dan delta opsi. */
  overrides?: PriceOverrideSpec[];
}

/** Geometri dari generator parametrik. Diabaikan untuk produk non-generator. */
export interface GeometrySpec {
  widthMm: number;
  heightMm: number;
  cutLengthMm?: number | undefined;
}

export interface PriceInput {
  product: ProductPriceSpec;
  /** peta key opsi ke value terpilih, mis. { ukuran: '5x5', bahan: 'chrome' } */
  selections: Record<string, string>;
  qty: number;
  geometry?: GeometrySpec;
}

export interface PriceBreakdown {
  basePrice: number;
  optionDelta: number;
  optionFactor: number;
  areaFactor: number;
  /**
   * Harga satuan dari baris override yang cocok, sebelum luas dan tier.
   * null berarti harga dihitung dari basePrice dan delta opsi, sehingga
   * optionDelta dan optionFactor itulah yang menentukan hasil.
   */
  overrideUnitPrice: number | null;
  /** Harga coret sesudah luas dan tier. null berarti tidak ada harga coret. */
  compareAtPrice: number | null;
  unitPriceBeforeTier: number;
  tierFactor: number;
  tierMinQty: number;
  unitPrice: number;
  qty: number;
  total: number;
}

export class PriceError extends Error {
  constructor(
    public readonly code:
      | 'QTY_BELOW_MIN'
      | 'QTY_INVALID_STEP'
      | 'OPTION_REQUIRED'
      | 'OPTION_UNKNOWN'
      | 'VALUE_UNKNOWN'
      | 'GEOMETRY_REQUIRED'
      | 'AREA_UNRESOLVED',
    message: string,
  ) {
    super(message);
    this.name = 'PriceError';
  }
}

const MM2_PER_M2 = 1_000_000;
const MM_PER_M = 1_000;

/** Pembulatan setengah ke atas, tahan galat floating point. */
function roundRupiah(n: number): number {
  return Math.round(Number(n.toFixed(6)));
}

/**
 * Override yang cocok dan paling spesifik.
 *
 * Cocok berarti setiap pasangan key dan value pada override sama persis dengan
 * pilihan pelanggan. Bila lebih dari satu cocok, yang menyebut paling banyak
 * opsi yang dipakai, supaya baris khusus mengalahkan baris umum.
 */
function resolveOverride(
  overrides: PriceOverrideSpec[] | undefined,
  selections: Record<string, string>,
): PriceOverrideSpec | null {
  if (!overrides || overrides.length === 0) return null;

  let best: PriceOverrideSpec | null = null;
  let bestSize = -1;

  for (const ov of overrides) {
    const keys = Object.keys(ov.selections);
    if (keys.length === 0) continue;
    let cocok = true;
    for (const key of keys) {
      if (selections[key] !== ov.selections[key]) {
        cocok = false;
        break;
      }
    }
    if (cocok && keys.length > bestSize) {
      best = ov;
      bestSize = keys.length;
    }
  }

  return best;
}

function resolveTier(tiers: TierSpec[], qty: number): TierSpec {
  const sorted = [...tiers].sort((a, b) => a.minQty - b.minQty);
  let hit: TierSpec = { minQty: 0, factor: 1 };
  for (const t of sorted) {
    if (qty >= t.minQty) hit = t;
  }
  return hit;
}

/**
 * Faktor luas atau panjang. Bernilai 1 untuk produk yang dijual per pcs.
 * Sumber dimensi: geometri generator lebih diutamakan daripada opsi ukuran.
 */
function resolveAreaFactor(
  unit: PriceUnit,
  dims: { widthMm: number | null; heightMm: number | null } | null,
  geometry?: GeometrySpec,
): number {
  if (unit === 'PIECE') return 1;

  const w = geometry?.widthMm ?? dims?.widthMm ?? null;
  const h = geometry?.heightMm ?? dims?.heightMm ?? null;

  if (w === null || w <= 0) {
    throw new PriceError(
      'AREA_UNRESOLVED',
      'Lebar tidak diketahui untuk produk yang dijual per satuan luas atau panjang.',
    );
  }

  if (unit === 'LINEAR_METER') return w / MM_PER_M;

  if (h === null || h <= 0) {
    throw new PriceError(
      'AREA_UNRESOLVED',
      'Tinggi tidak diketahui untuk produk yang dijual per meter persegi.',
    );
  }
  return (w * h) / MM2_PER_M2;
}

export function calculatePrice(input: PriceInput): PriceBreakdown {
  const { product, selections, qty, geometry } = input;

  if (!Number.isInteger(qty) || qty < product.minQty) {
    throw new PriceError('QTY_BELOW_MIN', `Jumlah minimal ${product.minQty}.`);
  }
  const step = product.qtyStep > 0 ? product.qtyStep : 1;
  if ((qty - product.minQty) % step !== 0) {
    throw new PriceError(
      'QTY_INVALID_STEP',
      `Jumlah harus kelipatan ${step} dari ${product.minQty}.`,
    );
  }

  const known = new Set(product.options.map((o) => o.key));
  for (const key of Object.keys(selections)) {
    if (!known.has(key)) {
      throw new PriceError('OPTION_UNKNOWN', `Opsi "${key}" tidak dikenal pada produk ini.`);
    }
  }

  let optionDelta = 0;
  let optionFactor = 1;
  let dims: { widthMm: number | null; heightMm: number | null } | null = null;

  for (const opt of product.options) {
    const picked = selections[opt.key];
    if (picked === undefined || picked === '') {
      if (opt.required) {
        throw new PriceError('OPTION_REQUIRED', `Opsi "${opt.key}" wajib dipilih.`);
      }
      continue;
    }
    const val = opt.values.find((v) => v.value === picked);
    if (!val) {
      throw new PriceError(
        'VALUE_UNKNOWN',
        `Pilihan "${picked}" tidak tersedia untuk opsi "${opt.key}".`,
      );
    }
    optionDelta += val.priceDelta;
    optionFactor *= val.priceFactor;
    if (val.widthMm !== null && val.widthMm !== undefined) {
      dims = { widthMm: val.widthMm ?? null, heightMm: val.heightMm ?? null };
    }
  }

  if (product.priceUnit !== 'PIECE' && !dims && !geometry) {
    throw new PriceError('GEOMETRY_REQUIRED', 'Produk ini memerlukan ukuran untuk dihitung.');
  }

  const areaFactor = resolveAreaFactor(product.priceUnit, dims, geometry);

  // Override menggantikan basePrice dan delta opsi, tetapi tetap tunduk pada
  // faktor luas dan tier jumlah: keduanya soal ukuran dan volume, bukan kombinasi.
  const override = resolveOverride(product.overrides, selections);
  const unitBefore =
    override === null
      ? (product.basePrice + optionDelta) * optionFactor * areaFactor
      : override.unitPrice * areaFactor;

  const tier = resolveTier(product.tiers, qty);
  const unitPrice = roundRupiah(unitBefore * tier.factor);

  const compareAtRaw = override?.compareAtPrice ?? product.compareAtPrice ?? null;
  const compareAtPrice =
    compareAtRaw === null || compareAtRaw <= 0
      ? null
      : roundRupiah(compareAtRaw * areaFactor * tier.factor);

  return {
    basePrice: product.basePrice,
    optionDelta,
    optionFactor,
    areaFactor,
    overrideUnitPrice: override === null ? null : override.unitPrice,
    compareAtPrice: compareAtPrice !== null && compareAtPrice > unitPrice ? compareAtPrice : null,
    unitPriceBeforeTier: roundRupiah(unitBefore),
    tierFactor: tier.factor,
    tierMinQty: tier.minQty,
    unitPrice,
    qty,
    total: unitPrice * qty,
  };
}
